export type StatusVariant =
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'neutral';

export function formatDateTime(value: Date | string | null | undefined) {
  if (!value) {
    return 'Never';
  }

  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) {
    return 'Unknown';
  }

  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatRelative(value: Date | string | null | undefined) {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) {
    return null;
  }

  const diffMs = Date.now() - date.getTime();
  const future = diffMs < 0;
  const absSeconds = Math.round(Math.abs(diffMs) / 1000);

  if (absSeconds < 45) {
    return future ? 'in moments' : 'just now';
  }
  const minutes = Math.round(absSeconds / 60);
  if (minutes < 60) {
    return future ? `in ${minutes}m` : `${minutes}m ago`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return future ? `in ${hours}h` : `${hours}h ago`;
  }
  const days = Math.round(hours / 24);
  if (days < 14) {
    return future ? `in ${days}d` : `${days}d ago`;
  }
  const weeks = Math.round(days / 7);
  if (weeks < 8) {
    return future ? `in ${weeks}w` : `${weeks}w ago`;
  }
  const months = Math.round(days / 30);
  if (months < 12) {
    return future ? `in ${months}mo` : `${months}mo ago`;
  }
  const years = Math.round(days / 365);
  return future ? `in ${years}y` : `${years}y ago`;
}

export function formatWindow(
  windowStart: Date | string | null | undefined,
  windowEnd: Date | string | null | undefined,
) {
  if (!windowStart || !windowEnd) {
    return 'Unknown window';
  }

  return `${formatDateTime(windowStart)} → ${formatDateTime(windowEnd)}`;
}

export function formatDuration(
  startedAt: Date | string | null | undefined,
  completedAt: Date | string | null | undefined,
) {
  if (!startedAt || !completedAt) {
    return null;
  }

  const start = startedAt instanceof Date ? startedAt : new Date(startedAt);
  const end = completedAt instanceof Date ? completedAt : new Date(completedAt);

  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) {
    return null;
  }

  const totalSeconds = Math.max(
    0,
    Math.round((end.getTime() - start.getTime()) / 1000),
  );
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) {
    return `${seconds}s`;
  }
  return `${minutes}m ${seconds}s`;
}

type StatusDisplayMeta = {
  label: string;
  variant: StatusVariant;
};

const RUN_STATUS_META: Record<string, StatusDisplayMeta> = {
  Completed: { label: 'Completed', variant: 'success' },
  CompletedWithErrors: { label: 'Completed with errors', variant: 'warning' },
  Error: { label: 'Error', variant: 'error' },
  Running: { label: 'Running', variant: 'info' },
};

const APP_STATUS_META: Record<string, StatusDisplayMeta> = {
  Completed: { label: 'Completed', variant: 'success' },
  NoUsefulReviews: { label: 'No useful reviews', variant: 'warning' },
  NoReviews: { label: 'No reviews', variant: 'neutral' },
  SourceUnavailable: { label: 'Source unavailable', variant: 'warning' },
  Error: { label: 'Error', variant: 'error' },
};

const EMAIL_STATUS_META: Record<string, StatusDisplayMeta> = {
  Sent: { label: 'Sent', variant: 'success' },
  Sending: { label: 'Sending', variant: 'info' },
  Pending: { label: 'Pending', variant: 'info' },
  Failed: { label: 'Failed', variant: 'error' },
  NotRequested: { label: 'Not requested', variant: 'neutral' },
  NotEligible: { label: 'Not eligible', variant: 'neutral' },
  SkippedWeeklyLimit: { label: 'Weekly limit', variant: 'warning' },
  SkippedMissingPublisher: { label: 'Missing publisher', variant: 'neutral' },
  SkippedMissingRecipient: { label: 'Missing recipient', variant: 'neutral' },
  SkippedPublisherConsentMissing: {
    label: 'Consent missing',
    variant: 'warning',
  },
};

function statusVariantFrom(
  meta: Record<string, StatusDisplayMeta>,
  status: string | null | undefined,
) {
  if (!status) {
    return 'neutral';
  }
  return meta[status]?.variant ?? 'neutral';
}

function statusLabelFrom(
  meta: Record<string, StatusDisplayMeta>,
  status: string | null | undefined,
) {
  if (status == null) {
    return 'Unknown';
  }
  return meta[status]?.label ?? status;
}

export function getRunStatusVariant(status: string | null | undefined) {
  return statusVariantFrom(RUN_STATUS_META, status);
}

export function getAppStatusVariant(status: string | null | undefined) {
  return statusVariantFrom(APP_STATUS_META, status);
}

export function getEmailStatusVariant(status: string | null | undefined) {
  return statusVariantFrom(EMAIL_STATUS_META, status);
}

export function formatAppStatus(status: string | null | undefined) {
  return statusLabelFrom(APP_STATUS_META, status);
}

export function formatEmailStatus(status: string | null | undefined) {
  return statusLabelFrom(EMAIL_STATUS_META, status);
}

export function formatRunStatus(status: string | null | undefined) {
  return statusLabelFrom(RUN_STATUS_META, status);
}

const STATUS_TONE_CLASSES: Record<StatusVariant, string> = {
  success: 'border-emerald-400/20 bg-emerald-400/10 text-emerald-200',
  warning: 'border-amber-400/20 bg-amber-400/10 text-amber-200',
  error: 'border-rose-400/20 bg-rose-400/10 text-rose-200',
  info: 'border-sky-400/20 bg-sky-400/10 text-sky-200',
  neutral: 'border-white/10 bg-white/5 text-neutral-300',
};

export function statusToneClass(variant: StatusVariant) {
  return STATUS_TONE_CLASSES[variant];
}
